import { flow, getEnv, Instance, types } from "mobx-state-tree";

import { getTranslationKeyForType, SewerObject } from "./SewerObject";
import { moveItemInArray } from "../../helpers/ArrayHelpers";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import { Column, ColumnType } from "../../components/datatable/interfaces";
import { sortColumns } from "../../components/datatable/helpers/sortExtensions";
import { endpoints } from "../api/ApiEndpoints";
import { PROFIL_BREITE_CODE, HOHE_LANGE_CODE } from "../../api-models/response/BluemetricFields";
import {
  DataGridResponseWithDynamicProperties,
  HeaderItemResponse,
  RowResponse,
} from "../../api-models/response/DataGridResponse";
import { SewerObjectType } from "../../generated/SewerObjects";

const esicTableColumns: Column[] = [
  {
    key: "Name",
    name: "general.sewer-objects.list-header.sewer-object-id",
    type: ColumnType.String,
    sortable: true,
    filterable: true,
    containsLink: true,
  },
  {
    key: "Type",
    name: "general.type",
    type: ColumnType.SingleSelect,
    valueOptions: [
      {
        label: getTranslationKeyForType(SewerObjectType.Section),
        value: SewerObjectType.Section.toString(),
      },
      {
        label: getTranslationKeyForType(SewerObjectType.Node),
        value: SewerObjectType.Node.toString(),
      },
      {
        label: getTranslationKeyForType(SewerObjectType.Structure),
        value: SewerObjectType.Structure.toString(),
      },
    ],
    sortable: true,
    filterable: true,
  },
  {
    key: "InspectionsCount",
    name: "general.inspections",
    type: ColumnType.Number,
    sortable: true,
    filterable: true,
  },
  {
    key: "Project",
    name: "general.project",
    type: ColumnType.String,
    sortable: true,
    filterable: true,
    containsLink: true,
  },
  {
    key: "Modified",
    name: "general.last-edit",
    type: ColumnType.DateTime,
    sortable: true,
    filterable: true,
  },
];

export const JobSewerObjectsStorageModel = types
  .compose(
    types.model({
      jobId: types.number,
    }),
    DataGridStorageModel
  )
  .actions((self) => {
    const getHeaders = (response: DataGridResponseWithDynamicProperties) => {
      const sewerObjectsHeaders = response.header as HeaderItemResponse[];
      if (!sewerObjectsHeaders.length) return esicTableColumns;

      sewerObjectsHeaders.sort(sortColumns);
      const filteredHeaders = sewerObjectsHeaders.filter((header) => header.key !== "Organization");

      const BMColumns = filteredHeaders
        .filter((header) => esicTableColumns.every((x) => x.key !== header.key))
        .map((column: HeaderItemResponse) => {
          return {
            key: column.key,
            name: column.name,
            type: self.getColumnType(column.metadata),
            valueOptions: self.getColumnPredefinedValueOptions(column.metadata),
            sortable: column.key !== PROFIL_BREITE_CODE && column.key !== HOHE_LANGE_CODE,
            filterable: column.key !== PROFIL_BREITE_CODE && column.key !== HOHE_LANGE_CODE,
          };
        });
      const allColumns = esicTableColumns.concat(BMColumns);

      const modifiedColumnIndex = allColumns.findIndex((x) => x.key === "Modified");
      moveItemInArray(allColumns, modifiedColumnIndex, filteredHeaders.length - 1);

      return allColumns;
    };

    return {
      afterCreate() {
        self.defaultSort = { field: "Name", sort: "asc" };
      },
      mapItem(item: RowResponse) {
        return SewerObject._constructorFromRowItem(item, `/jobs/${self.jobId}`);
      },
      mapResponse(response: DataGridResponseWithDynamicProperties) {
        self.data = {
          totalCount: response.totalCount,
          items: response.rows.map(self.mapItem),
        };
        self.gridColumns = getHeaders(response);
      },
      setJobId(jobId: number) {
        self.jobId = jobId;
      },
      assignSewerObjectsToJob: flow(function* (jobId, sewerObjectIds) {
        const endpoint = endpoints.get("job_sewer_objects");
        if (endpoint) endpoint.routeParams = [jobId];
        const response = yield getEnv(self).api.post(endpoint, sewerObjectIds);
        yield self.load();
        return response;
      }),
      removeSewerObjectsFromJob: flow(function* (jobId, ids: number[]) {
        const endpoint = endpoints.get("job_sewer_objects");
        if (endpoint) endpoint.routeParams = [jobId];
        const response = yield getEnv(self).api.delete(endpoint, ids);
        self.deselectAll();
        yield self.load();
        return response;
      }),
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("job_sewer_objects_all");
      endpoint.routeParams = [self.jobId];
      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
    get hasSewerObjectsAfterDelete() {
      return self.data.totalCount - self.selectedItemsCount > 0;
    },
  }));

export type JobSewerObjectsStorage = Instance<typeof JobSewerObjectsStorageModel>;
